// src/pages/carbon-offset-dashboard/components/CarbonFootprintChart.jsx
import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import Icon from 'components/AppIcon';

const CarbonFootprintChart = ({ data, timeRange }) => {
  const [chartType, setChartType] = useState('area');
  const [showTrend, setShowTrend] = useState(true);

  const chartTypes = [
    { id: 'area', label: 'Area Chart', icon: 'BarChart3' },
    { id: 'line', label: 'Line Chart', icon: 'TrendingUp' },
    { id: 'bar', label: 'Bar Chart', icon: 'BarChart2' }
  ];

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const emissions = payload.find(p => p.dataKey === 'emissions')?.value || 0;
      const offset = payload.find(p => p.dataKey === 'offset')?.value || 0;
      const coverage = offset > 0 ? ((offset / emissions) * 100).toFixed(1) : 0;
      const remaining = Math.max(0, emissions - offset);

      return (
        <div className="bg-surface border border-border rounded-lg shadow-lg p-4 min-w-[200px]">
          <h4 className="text-sm font-medium text-text-primary mb-3">{label}</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-error rounded-full"></div>
                <span className="text-sm text-text-secondary">Total Emissions:</span>
              </div>
              <span className="text-sm font-medium text-text-primary">{emissions} tonnes</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full"></div>
                <span className="text-sm text-text-secondary">Offset:</span>
              </div>
              <span className="text-sm font-medium text-text-primary">{offset} tonnes</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Coverage:</span>
                <span className="text-sm font-medium text-success">{coverage}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Remaining:</span>
                <span className="text-sm font-medium text-warning">{remaining.toFixed(1)} tonnes</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: data.emissions,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              label={{ value: 'Tonnes CO2e', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={customTooltip} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="emissions" 
              stroke="#EF4444" 
              strokeWidth={3}
              dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 2 }}
              name="Total Emissions"
            />
            <Line 
              type="monotone" 
              dataKey="offset" 
              stroke="#10B981" 
              strokeWidth={3}
              dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 2 }}
              name="Carbon Offset"
            />
          </LineChart>
        );
        
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              label={{ value: 'Tonnes CO2e', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={customTooltip} />
            <Legend />
            <Bar 
              dataKey="emissions" 
              fill="#EF4444" 
              name="Total Emissions"
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="offset" 
              fill="#10B981" 
              name="Carbon Offset"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        );
        
      default: // area
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="emissionsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="offsetGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              label={{ value: 'Tonnes CO2e', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={customTooltip} />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="emissions" 
              stroke="#EF4444" 
              strokeWidth={2}
              fill="url(#emissionsGradient)"
              name="Total Emissions"
            />
            <Area 
              type="monotone" 
              dataKey="offset" 
              stroke="#10B981" 
              strokeWidth={2}
              fill="url(#offsetGradient)"
              name="Carbon Offset"
            />
          </AreaChart>
        );
    }
  };

  const totalEmissions = data.emissions.reduce((sum, item) => sum + item.emissions, 0);
  const totalOffset = data.emissions.reduce((sum, item) => sum + item.offset, 0);
  const averageCoverage = totalOffset > 0 ? ((totalOffset / totalEmissions) * 100).toFixed(1) : 0;

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Carbon Emissions vs Offsets</h3>
          <p className="text-sm text-text-secondary mt-1">
            Track your carbon emissions and offset coverage over time
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Chart Type Selector */}
          <div className="flex items-center space-x-1 bg-surface-50 rounded-md p-1">
            {chartTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setChartType(type.id)}
                className={`
                  flex items-center space-x-1 px-3 py-1 rounded text-sm font-medium transition-all duration-150
                  ${
                    chartType === type.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:text-primary hover:bg-primary-50'
                  }
                `}
                title={type.label}
              >
                <Icon name={type.icon} size={14} />
                <span className="hidden sm:inline">{type.label}</span>
              </button>
            ))}
          </div>
          
          {/* Toggle for trend line */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-trend"
              checked={showTrend}
              onChange={(e) => setShowTrend(e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary-500"
            />
            <label htmlFor="show-trend" className="text-sm text-text-secondary cursor-pointer">
              Show Trend
            </label>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-error-50 rounded-lg p-4 border border-error-100">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Factory" size={16} className="text-error" />
            <span className="text-sm font-medium text-error uppercase tracking-wide">Total Emissions</span>
          </div>
          <p className="text-2xl font-bold text-error">
            {totalEmissions.toFixed(1)} <span className="text-sm font-normal">tonnes</span>
          </p>
        </div>
        
        <div className="bg-success-50 rounded-lg p-4 border border-success-100">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Leaf" size={16} className="text-success" />
            <span className="text-sm font-medium text-success uppercase tracking-wide">Total Offset</span>
          </div>
          <p className="text-2xl font-bold text-success">
            {totalOffset.toFixed(1)} <span className="text-sm font-normal">tonnes</span>
          </p>
        </div>
        
        <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wide">Coverage</span>
          </div>
          <p className="text-2xl font-bold text-primary">
            {averageCoverage}% <span className="text-sm font-normal">average</span>
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* Chart Legend */}
      <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded-full"></div>
          <span className="text-sm text-text-secondary">Total Emissions</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded-full"></div>
          <span className="text-sm text-text-secondary">Carbon Offset</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-warning rounded-full"></div>
          <span className="text-sm text-text-secondary">Remaining Emissions</span>
        </div>
      </div>
    </div>
  );
};

export default CarbonFootprintChart;